import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RecoilValue, useRecoilValue, useSetRecoilState } from 'recoil';
import {
    AnnotationFrameChange,
    AnnotationPlane,
    AnnotationRecord,
} from '~/domain/annotation/annotation';
import { PointMm } from '~/domain/geometry/patientGeometry';
import { MOUSE_BUTTON_LEFT } from '~/engine/tool/MouseButton';
import { paneImageOf } from '~/engine/view/paneImage';
import {
    arePointsOnDisplayedSlice,
    clientToPatientPoint,
    displayedSliceToleranceMm,
    imageToPaneFrame,
    pinToViewPlane,
    viewPlaneCoordinateMm,
    viewShowsPlane,
} from '~/engine/view/planeViewGeometry';
import type { CanvasFrameGeometry } from '~/engine/view/TextureView';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { ViewManager } from '~/engine/view/ViewManager';
import { useWindowMouseDrag } from '~/hooks/useWindowMouseDrag';
import {
    hoveredAnnotationState,
    isAnnotationHoveredState,
    isAnnotationSelectedState,
    selectedAnnotationState,
    unlessNaming,
} from '~/state/atoms/annotationListAtoms';
import type { Tools } from '~/state/atoms/toolsAtoms';

/**
 * What placed annotations respond to: bands, labels and handles (`edit`), the
 * labels and the selected one's handles (`select`), or nothing, as while a
 * shape is being placed, whose next click belongs to the canvas.
 */
export type AnnotationInteraction = 'edit' | 'select' | 'none';

const INTERACTION_OF: Partial<Record<Tools, AnnotationInteraction>> = {
    measurement: 'edit',
    intensity: 'edit',
    pan: 'select',
};

export function annotationInteraction(
    tool: Tools,
    placementActive: boolean
): AnnotationInteraction {
    return placementActive ? 'none' : (INTERACTION_OF[tool] ?? 'none');
}

/** The redraw events that can move a view's camera: scroll, pan and zoom. */
export const CAMERA_REDRAW_EVENTS: readonly string[] = [
    'planeIntersectionPointChanged',
    'zoomScaleChanged',
];

/** The redraw events that move no camera, so no published frame announces. */
const FRAME_INDEPENDENT_REDRAW_EVENTS: readonly string[] = [
    'fusedImageDisplayChanged',
    'registration_changed',
];

/**
 * The view-manager events a world-anchored overlay must redraw for, and
 * that arrive WITHOUT a Recoil write or a new `view` object: a slice
 * scroll / programmatic navigation (`planeIntersectionPointChanged`); a
 * zoom — `PlaneView.updateZoomScale` dispatches ONLY `zoomScaleChanged`,
 * so an overlay listening to the intersection event alone sits on the
 * wrong anatomy after a zoom until the next scroll; a view changing sides
 * of a fusion (`fusedImageDisplayChanged`), which decides which series'
 * annotations are drawn on it; and a registration edit
 * (`registration_changed`), which moves the annotations of a fused
 * secondary drawn alone on the pane.
 */
const PLANE_REDRAW_EVENTS: readonly string[] = [
    ...CAMERA_REDRAW_EVENTS,
    ...FRAME_INDEPENDENT_REDRAW_EVENTS,
];

/** The redraw events of an overlay drawn over `frame`, or over the live view. */
export function redrawEventsOver(
    frame?: CanvasFrameGeometry
): readonly string[] {
    return frame ? FRAME_INDEPENDENT_REDRAW_EVENTS : PLANE_REDRAW_EVENTS;
}

/**
 * Re-render tick for an overlay's memoized projections: increments on
 * every occurrence of `events` (default: slice / pan / zoom / fusion side
 * / registration change, see {@link PLANE_REDRAW_EVENTS}). Include the
 * value in the projection memo's deps.
 *
 * A caller passing its own `events` must pass a render-stable array (a
 * module-level const): it is an effect dependency, so an inline literal
 * would resubscribe on every render.
 */
export function usePlaneRedrawTick(
    viewManager: ViewManager | null,
    events: readonly string[] = PLANE_REDRAW_EVENTS
): number {
    const [renderTick, setRenderTick] = useState(0);

    const forceUpdate = useCallback(() => {
        setRenderTick((prev) => prev + 1);
    }, []);

    useEffect(() => {
        if (!viewManager) {
            return;
        }
        for (const event of events) {
            viewManager.addEventListener(event, forceUpdate);
        }
        return () => {
            for (const event of events) {
                viewManager.removeEventListener(event, forceUpdate);
            }
        };
    }, [viewManager, events, forceUpdate]);

    return renderTick;
}

export interface AnnotationPointDrag<TDrag> {
    /** Begin dragging a point; call from a handle's `onMouseDown`. */
    readonly beginDrag: (event: React.MouseEvent, drag: TDrag) => void;
    /** The drag in progress, or null. */
    readonly dragging: TDrag | null;
}

/**
 * Drag interaction for editing one point of a committed annotation. Each move
 * unprojects with the same `clientToPatientCoordinates` the engine tool uses,
 * so there is no coordinate-space drift, then pins onto the plane through the
 * drag's `anchorMm` along the view's current normal, to keep the annotation's
 * points coplanar.
 *
 * The drag in progress is exposed so handles stay visible for its duration.
 */
export function useAnnotationPointDrag<
    TDrag extends { readonly anchorMm: PointMm },
>(
    view: PlaneView | null,
    onPointMoved: (drag: TDrag, pointMm: PointMm, view: PlaneView) => void
): AnnotationPointDrag<TDrag> {
    const dragRef = useRef<TDrag | null>(null);
    const [dragging, setDragging] = useState<TDrag | null>(null);

    const onMove = useCallback(
        (event: MouseEvent) => {
            const drag = dragRef.current;
            if (!drag || !view) {
                return;
            }
            const patientMm = clientToPatientPoint(
                [event.clientX, event.clientY],
                view
            );
            onPointMoved(
                drag,
                pinToViewPlane(
                    patientMm,
                    view,
                    viewPlaneCoordinateMm(drag.anchorMm, view)
                ),
                view
            );
        },
        [view, onPointMoved]
    );

    const onEnd = useCallback(() => {
        dragRef.current = null;
        setDragging(null);
    }, []);

    const startDrag = useWindowMouseDrag(onMove, onEnd);

    const beginDrag = useCallback(
        (event: React.MouseEvent, drag: TDrag) => {
            dragRef.current = drag;
            setDragging(drag);
            // useWindowMouseDrag stops propagation, so this never reaches the
            // engine, which would ignore it anyway: the target is the handle.
            startDrag(event);
        },
        [startDrag]
    );

    return { beginDrag, dragging };
}

/**
 * `item` moved from the frame of its own image onto the pane `view` shows,
 * through the registration read now, or null when the view does not show
 * the item's image in its orientation.
 */
export function ontoShownPane<T extends { readonly plane: AnnotationPlane }>(
    view: PlaneView,
    item: T,
    inFrame: (item: T, change: AnnotationFrameChange) => T
): T | null {
    const pane = paneImageOf(view);
    if (pane.kind === 'none' || !viewShowsPlane(view, item.plane)) {
        return null;
    }
    return inFrame(item, imageToPaneFrame(pane));
}

/**
 * Whether the pointer is on the elements of annotation `id`, and their
 * handlers. A left press selects it, reaching neither the canvas nor the
 * engine, then starts `beginDrag` if given. Pointing at it names it in
 * `hoveredAnnotationState` until the pointer leaves, or until the elements
 * stop being `interactive` or unmount, which sends no leave event: a Delete
 * would then remove the wrong shape.
 */
export function useAnnotationPointer(id: string, interactive: boolean) {
    const [entered, setEntered] = useState(false);
    const select = useSetRecoilState(selectedAnnotationState);
    const setHovered = useSetRecoilState(hoveredAnnotationState);
    if (entered && !interactive) {
        setEntered(false);
    }
    const inside = entered && interactive;

    useEffect(() => {
        if (!inside) {
            return;
        }
        setHovered(id);
        return () => setHovered(unlessNaming(id));
    }, [inside, id, setHovered]);

    return [
        inside,
        {
            onMouseEnter: () => setEntered(true),
            onMouseLeave: () => setEntered(false),
            onMouseDown: (
                event: React.MouseEvent,
                beginDrag?: (event: React.MouseEvent) => void
            ) => {
                if (event.button !== MOUSE_BUTTON_LEFT) {
                    return;
                }
                event.stopPropagation();
                event.preventDefault();
                select(id);
                beginDrag?.(event);
            },
        },
    ] as const;
}

/**
 * Annotation `id` of `list`, a list or any source of records by id, as stored
 * and as moved onto the pane `view` shows, or null when the view does not draw
 * it. Only its own record and flags are
 * read, so a shape re-renders for itself alone. Bands and handles are offered
 * only while the `pointsOf` it lie on the slice shown, in `frame` when given:
 * an edit keeps it on its plane, and a shape of another slice must not take
 * the presses that start a placement here.
 */
export function usePlacedAnnotation<T extends AnnotationRecord>(
    {
        id,
        view,
        frame,
        interaction,
    }: {
        id: string;
        view: PlaneView;
        frame?: CanvasFrameGeometry;
        interaction: AnnotationInteraction;
    },
    list: { readonly item: (id: string) => RecoilValue<T | undefined> },
    inFrame: (item: T, change: AnnotationFrameChange) => T,
    pointsOf: (item: T) => readonly PointMm[],
    dragged: boolean
) {
    const stored = useRecoilValue(list.item(id));
    const selected = useRecoilValue(isAnnotationSelectedState(id));
    const hovered = useRecoilValue(isAnnotationHoveredState(id));
    const onPane = stored?.visible && ontoShownPane(view, stored, inFrame);
    const onCurrentSlice =
        !!onPane &&
        arePointsOnDisplayedSlice(
            pointsOf(onPane),
            view,
            onPane.plane,
            displayedSliceToleranceMm(view, frame),
            frame
        );
    const bands = interaction === 'edit' && onCurrentSlice;
    const handles =
        onCurrentSlice &&
        (dragged || (interaction !== 'none' && selected) || (bands && hovered));
    const [, pointer] = useAnnotationPointer(id, bands || handles);
    return stored && onPane
        ? {
              stored,
              onPane,
              onCurrentSlice,
              selected,
              emphasised: selected || hovered || dragged,
              bands,
              handles,
              label: interaction !== 'none',
              pointer,
          }
        : null;
}

export type PlacedAnnotation<T extends AnnotationRecord = AnnotationRecord> =
    NonNullable<ReturnType<typeof usePlacedAnnotation<T>>>;
